import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';
import { DISCOVERY_PATH_PREFIX } from '../util/discoveryPaths';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() => import(/* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage'));
const CMSPage = loadable(() => import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ '../containers/EditListingPage/EditListingPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'));
const InboxPage = loadable(() => import(/* webpackChunkName: "InboxPage" */ '../containers/InboxPage/InboxPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage'));
const ListingPageCoverPhoto = loadable(() => import(/* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'));
const ListingPageCarousel = loadable(() => import(/* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'));
const ManageListingsPage = loadable(() => import(/* webpackChunkName: "ManageListingsPage" */ '../containers/ManageListingsPage/ManageListingsPage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const AboutPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/AboutPage/AboutPage'));
const HowitWorkPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ '../containers/HowItWork/FallbackPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'));
const SearchPageWithMap = loadable(() => import(/* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithMap'));
const SearchPageWithGrid = loadable(() => import(/* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */  '../containers/SearchPage/SearchPageWithGrid'));
const BrowseCategoryRedirectPage = loadable(() =>
  import(/* webpackChunkName: "BrowseCategoryRedirectPage" */ '../containers/BrowseCategoryPage/BrowseCategoryRedirectPage')
);
const SeoDiscoveryVenuesIndexPage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryVenuesIndexPage" */ '../containers/SeoDiscoveryVenuesIndexPage/SeoDiscoveryVenuesIndexPage')
);
const SeoDiscoveryCitiesIndexPage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryCitiesIndexPage" */ '../containers/SeoDiscoveryCitiesIndexPage/SeoDiscoveryCitiesIndexPage')
);
const SeoDiscoveryCitiesLetterPage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryCitiesLetterPage" */ '../containers/SeoDiscoveryCitiesLetterPage/SeoDiscoveryCitiesLetterPage')
);
const SeoDiscoveryCityPage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryCityPage" */ '../containers/SeoDiscoveryCityPage/SeoDiscoveryCityPage')
);
const SeoDiscoveryCitySubpage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryCitySubpage" */ '../containers/SeoDiscoveryCitySubpage/SeoDiscoveryCitySubpage')
);
const SeoDiscoveryPerformersIndexPage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryPerformersIndexPage" */ '../containers/SeoDiscoveryPerformersIndexPage/SeoDiscoveryPerformersIndexPage')
);
const SeoDiscoveryPerformersLetterPage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryPerformersLetterPage" */ '../containers/SeoDiscoveryPerformersLetterPage/SeoDiscoveryPerformersLetterPage')
);
const SeoDiscoveryPerformerPage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryPerformerPage" */ '../containers/SeoDiscoveryPerformerPage/SeoDiscoveryPerformerPage')
);
const SeoDiscoveryPerformerSubpage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryPerformerSubpage" */ '../containers/SeoDiscoveryPerformerSubpage/SeoDiscoveryPerformerSubpage')
);
const SeoDiscoveryVenuesLetterPage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryVenuesLetterPage" */ '../containers/SeoDiscoveryVenuesLetterPage/SeoDiscoveryVenuesLetterPage')
);
const SeoDiscoveryVenuePage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryVenuePage" */ '../containers/SeoDiscoveryVenuePage/SeoDiscoveryVenuePage')
);
const SeoDiscoveryVenueSubpage = loadable(() =>
  import(/* webpackChunkName: "SeoDiscoveryVenueSubpage" */ '../containers/SeoDiscoveryVenueSubpage/SeoDiscoveryVenueSubpage')
);
const StripePayoutPage = loadable(() => import(/* webpackChunkName: "StripePayoutPage" */ '../containers/StripePayoutPage/StripePayoutPage'));
const RewardsPage = loadable(() => import(/* webpackChunkName: "RewardsPage" */ '../containers/RewardsPage/RewardsPage'));
const AdminCustomerRewardsPage = loadable(() =>
  import(/* webpackChunkName: "AdminCustomerRewardsPage" */ '../containers/AdminCustomerRewardsPage/AdminCustomerRewardsPage')
);
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'));
const TransactionPage = loadable(() => import(/* webpackChunkName: "TransactionPage" */ '../containers/TransactionPage/TransactionPage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = ['StripePayoutPage', 'PaymentMethodsPage'];
// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="SearchPage" />;

const shouldKeepAtRoot = route => route.path === '/' && route.name === 'SearchPage';

const prefixDiscoveryRoute = route =>
  shouldKeepAtRoot(route)
    ? route
    : {
        ...route,
        path: `${DISCOVERY_PATH_PREFIX}${route.path}`,
      };

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig) => {
  const SearchPage =  SearchPageWithMap

  const ListingPage =  ListingPageCarousel


  const routes = [
    {
      path: '/',
      name: 'SearchPage',
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/browse/:slug',
      name: 'BrowseCategoryPage',
      component: BrowseCategoryRedirectPage,
    },
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage.loadData,
    },
    // {
    //   path: '/s',
    //   name: 'SearchPage',
    //   component: SearchPage,
    //   loadData: pageDataLoadingAPI.SearchPage.loadData,
    // },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      // Checkout is intentionally accessible for logged-out users so we can
      // support lower-friction guest onboarding within the flow.
      auth: false,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'details' }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/inbox',
      name: 'InboxBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      auth: true,
      authPage: 'LoginPage',
      component: InboxPage,
      loadData: pageDataLoadingAPI.InboxPage.loadData,
    },
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'customer' },
      loadData: (params, ...rest) =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }, ...rest),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="OrderDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: TransactionPage,
      extraProps: { transactionRole: 'provider' },
      loadData: pageDataLoadingAPI.TransactionPage.loadData,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      auth: true,
      authPage: 'LoginPage',
      component: props => <NamedRedirect name="SaleDetailsPage" params={{ id: props.params?.id }} />,
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="StripePayoutPage" />,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="StripePayoutPage" />,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="StripePayoutPage" />,
    },
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: StripePayoutPage,
      loadData: pageDataLoadingAPI.StripePayoutPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      path: '/account/rewards',
      name: 'RewardsPage',
      auth: true,
      authPage: 'LoginPage',
      component: RewardsPage,
    },
    {
      path: '/admin/customer-rewards',
      name: 'AdminCustomerRewardsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AdminCustomerRewardsPage,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
      loadData: pageDataLoadingAPI.TermsOfServicePage.loadData,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
      loadData: pageDataLoadingAPI.PrivacyPolicyPage.loadData,
    },
    {
      path: '/about',
      name: 'AboutPage',
      component: AboutPage,
      loadData: pageDataLoadingAPI.AboutPage.loadData,
    },
    {
      path: '/p/how-it-works',
      name: 'HowItWorkPage',
      component: HowitWorkPage,
      loadData: pageDataLoadingAPI.HowitWorkPage.loadData,
    },
    {
      path: '/p/how-it-works-buyers',
      name: 'HowItWorkPageBuyers',
      component: HowitWorkPage,
      loadData: pageDataLoadingAPI.HowitWorkPage.loadData,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/cities/letter/:letter/:page',
      name: 'SeoCitiesLetterPagePaged',
      component: SeoDiscoveryCitiesLetterPage,
      loadData: pageDataLoadingAPI.SeoCitiesLetterPage.loadData,
    },
    {
      path: '/cities/letter/:letter',
      name: 'SeoCitiesLetterPage',
      component: SeoDiscoveryCitiesLetterPage,
      loadData: pageDataLoadingAPI.SeoCitiesLetterPage.loadData,
    },
    {
      path: '/cities',
      name: 'SeoCitiesIndexPage',
      component: SeoDiscoveryCitiesIndexPage,
      loadData: pageDataLoadingAPI.SeoCitiesIndexPage.loadData,
    },
    {
      path: '/cities/:citySlug/:subpageSlug',
      name: 'SeoCitySubpage',
      component: SeoDiscoveryCitySubpage,
      loadData: pageDataLoadingAPI.SeoCitySubpage.loadData,
    },
    {
      path: '/cities/:citySlug',
      name: 'SeoCityPage',
      component: SeoDiscoveryCityPage,
      loadData: pageDataLoadingAPI.SeoCityPage.loadData,
    },
    {
      path: '/performers/letter/:letter/:page',
      name: 'SeoPerformersLetterPagePaged',
      component: SeoDiscoveryPerformersLetterPage,
      loadData: pageDataLoadingAPI.SeoPerformersLetterPage.loadData,
    },
    {
      path: '/performers/letter/:letter',
      name: 'SeoPerformersLetterPage',
      component: SeoDiscoveryPerformersLetterPage,
      loadData: pageDataLoadingAPI.SeoPerformersLetterPage.loadData,
    },
    {
      path: '/performers',
      name: 'SeoPerformersIndexPage',
      component: SeoDiscoveryPerformersIndexPage,
      loadData: pageDataLoadingAPI.SeoPerformersIndexPage.loadData,
    },
    {
      path: '/performers/:performerSlug/:subpageSlug',
      name: 'SeoPerformerSubpage',
      component: SeoDiscoveryPerformerSubpage,
      loadData: pageDataLoadingAPI.SeoPerformerSubpage.loadData,
    },
    {
      path: '/performers/:performerSlug',
      name: 'SeoPerformerPage',
      component: SeoDiscoveryPerformerPage,
      loadData: pageDataLoadingAPI.SeoPerformerPage.loadData,
    },
    // Legacy /artists/ → /performers/ redirects
    {
      path: '/artists/:performerSlug/:subpageSlug',
      name: 'SeoArtistsSubpageRedirect',
      component: props => (
        <NamedRedirect
          name="SeoPerformerSubpage"
          params={{
            performerSlug: props.params?.performerSlug,
            subpageSlug: props.params?.subpageSlug,
          }}
        />
      ),
    },
    {
      path: '/artists/:performerSlug',
      name: 'SeoArtistsPageRedirect',
      component: props => (
        <NamedRedirect
          name="SeoPerformerPage"
          params={{ performerSlug: props.params?.performerSlug }}
        />
      ),
    },
    {
      path: '/artists',
      name: 'SeoArtistsIndexRedirect',
      component: () => <NamedRedirect name="SeoPerformersIndexPage" />,
    },
    {
      path: '/venues/letter/:letter/:page',
      name: 'SeoVenuesLetterPagePaged',
      component: SeoDiscoveryVenuesLetterPage,
      loadData: pageDataLoadingAPI.SeoVenuesLetterPage.loadData,
    },
    {
      path: '/venues/letter/:letter',
      name: 'SeoVenuesLetterPage',
      component: SeoDiscoveryVenuesLetterPage,
      loadData: pageDataLoadingAPI.SeoVenuesLetterPage.loadData,
    },
    {
      path: '/venues',
      name: 'SeoVenuesIndexPage',
      component: SeoDiscoveryVenuesIndexPage,
      loadData: pageDataLoadingAPI.SeoVenuesIndexPage.loadData,
    },
    {
      path: '/venues/:venueSlug/:subpageSlug',
      name: 'SeoVenueSubpage',
      component: SeoDiscoveryVenueSubpage,
      loadData: pageDataLoadingAPI.SeoVenueSubpage.loadData,
    },
    {
      path: '/venues/:venueSlug',
      name: 'SeoVenuePage',
      component: SeoDiscoveryVenuePage,
      loadData: pageDataLoadingAPI.SeoVenuePage.loadData,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Do not change this relative path!
    //
    // The route-prefix helper exposes it at /event-discovery/reset-password.
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this relative path!
    //
    // The route-prefix helper exposes it at /event-discovery/verify-email.
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
    // Do not change this relative path!
    //
    // The route-prefix helper exposes it at /event-discovery/preview.
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },
  ];

  return routes.map(prefixDiscoveryRoute);
};

export default routeConfiguration;
